import React, { useEffect, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { exchangeService } from '../../services/api';

const STATUS_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: 'true', label: 'Attive' },
  { value: 'false', label: 'Non attive' },
];

const AllExchangeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (isActive !== '') params.is_active = isActive;
      if (search.trim()) params.search = search.trim();
      const res = await exchangeService.list(params);
      setRequests(res.data || []);
    } catch {
      setError('Errore nel caricamento delle richieste.');
    } finally {
      setLoading(false);
    }
  }, [isActive, search]);

  useEffect(() => {
    load();
  }, [load]);

  const resetFilters = () => {
    setSearch('');
    setIsActive('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const getStatusChip = (req) => {
    if (req.status === 'accepted') {
      return <Chip label="Accettata" color="success" size="small" variant="outlined" />;
    }
    if (req.status === 'rejected') {
      return <Chip label="Rifiutata" color="error" size="small" variant="outlined" />;
    }
    return <Chip label="In attesa" color="warning" size="small" variant="outlined" />;
  };

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 4, md: 8 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3 }}>
          <Button
            component={Link}
            to="/dashboard"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="primary"
            sx={{ alignSelf: 'flex-start', mb: 1, textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Typography component="h1" variant="h4" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%' }}>
            Tutte le richieste di scambio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Totale: {requests.length} richieste
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Filtri */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
          <TextField
            label="Cerca per nome, email o descrizione"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Stato</InputLabel>
            <Select value={isActive} label="Stato" onChange={(e) => setIsActive(e.target.value)}>
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={resetFilters} sx={{ textTransform: 'none' }} size="small">
            Reset filtri
          </Button>
        </Box>

        {!isMobile ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Richiedente</TableCell>
                  <TableCell>Destinatario</TableCell>
                  <TableCell>Descrizione</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography align="center" sx={{ py: 2 }}>Caricamento…</Typography>
                    </TableCell>
                  </TableRow>
                ) : requests.length > 0 ? (
                  requests.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Typography variant="body2">{r.requester_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.requester_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.recipient_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.recipient_email}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {r.description}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(r)}</TableCell>
                      <TableCell>{formatDate(r.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography align="center" sx={{ py: 2 }} color="text.secondary">
                        Nessuna richiesta trovata.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : loading ? (
          <Typography align="center" sx={{ py: 3 }}>Caricamento…</Typography>
        ) : requests.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {requests.map((r) => (
              <Paper key={r.id} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {getStatusChip(r)}
                </Box>
                <Typography variant="caption" color="text.secondary">Richiedente</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{r.requester_name} — {r.requester_email}</Typography>
                <Typography variant="caption" color="text.secondary">Destinatario</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{r.recipient_name} — {r.recipient_email}</Typography>
                <Typography variant="caption" color="text.secondary">Descrizione</Typography>
                <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.description}</Typography>
                <Typography variant="caption" color="text.secondary">Data: {formatDate(r.created_at)}</Typography>
              </Paper>
            ))}
          </Box>
        ) : (
          <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
            Nessuna richiesta trovata.
          </Typography>
        )}
      </Paper>

    </Container>
  );
};

export default AllExchangeRequests;
